<template lang="pug">
  .transmitter-detail
    .tabs
      el-radio-group.mb-3(
        v-model="activeTab"
        fill="#2e7d32"
        text-color="#ffffff"
      )
        el-radio-button(label="status") {{ $t('transmitters.status.tab_status') }}
        el-radio-button(label="config") {{ $t('transmitters.status.tab_config') }}
        el-radio-button(label="power") {{ $t('transmitters.status.tab_power') }}

    .status-tab(v-show="activeTab === 'status'")
      PowerProfileStatus(v-if="transmitter" :transmitter="transmitter" :refreshing="refreshing" @refresh="refreshTransmitter")
      .status-card
        .status-card-column
          .status-card-row
            i.el-icon-connection.status-icon
            span.status-label {{ $t('transmitters.status.sensors_count') }}
            span.status-value {{ transmitter && transmitter.sensors_count ? transmitter.sensors_count : '-' }}
        .status-card-column
          .status-card-row
            i.el-icon-cpu.status-icon
            span.status-label {{ $t('transmitters.status.device_firmware') }}
            span.status-value {{ transmitter.firmware_version ? buildDateTime(transmitter.firmware_version) : '-' }}
            el-tag.ml-2(
              :type="firmwareOutdated ? 'warning' : 'success'"
              effect="dark"
              size="small"
            )
              i.el-icon-warning-outline.mr-1(v-if="firmwareOutdated")
              i.el-icon-circle-check.mr-1(v-else)
              span {{ firmwareOutdated ? $t('transmitters.status.update_pending') : $t('transmitters.status.up_to_date') }}
          .status-card-row
            i.el-icon-upload2.status-icon
            span.status-label {{ $t('transmitters.status.site_latest_firmware') }}
            span.status-value {{ transmitter.site_latest_firmware_build_time ? buildDateTime(transmitter.site_latest_firmware_build_time) : '-' }}
    .edit-transmitter-config(v-show="activeTab === 'config'")
      el-form(
        :model="form_config"
        v-loading="loadingForm"
        ref="EditTransmitterConfigForm"
        :rules="editTransmitterConfigRules"
        @submit.native.prevent
      )
        el-row(:gutter="24")
          el-col(:span="8")
            el-form-item(
              prop="power_profile_id"
              :label="$t('powerProfiles.actions.label.power_profile')"
            )
              el-select.w-100(
                v-model='form_config.power_profile_id'
                filterable
                :placeholder="$t('powerProfiles.actions.label.power_profile')"
              )
                el-option(
                  v-for='powerProfile in powerProfiles'
                  :key='powerProfile.id'
                  :label='`Profile ${powerProfile.profile_number} (${powerProfile.execution_time_human})`'
                  :value='powerProfile.id'
                )
          el-col(:span="6")
            el-form-item(
              prop="execution_count"
              :label="$t('powerProfiles.actions.label.execution_count')"
            )
              el-select.w-100(
                v-model='form_config.execution_count'
                filterable
                :placeholder="$t('powerProfiles.actions.label.execution_count')"
              )
                el-option(:key="0" :label="$t('powerProfiles.actions.permanent')" :value="0")
                el-option(v-for="n in 255" :key="n" :label="String(n)" :value="n")
        .pps-hint
          i.el-icon-warning-outline
          span {{ $t('powerProfiles.actions.count_only_change_warning') }}
      el-row.text-center
        el-button.dark-green-btn.mt-5.pop-up-btn(
          @click="handleEditTransmitterConfig('EditTransmitterConfigForm')"
          :loading="loadingSubmitBtn"
        ) {{ $t('common.edit') }}
    .status-dialog(v-show="activeTab === 'power'")
      el-radio-group.mb-3(
        v-model="category"
        fill="#93c572"
        text-color="#ffffff"
        @change="handleCategoryChange"
      )
        el-radio-button(label="general_info" :title="$t('transmitters.status.general_info_title')") {{ $t('transmitters.status.general_info') }}
        el-radio-button(label="heartbeat" :title="$t('transmitters.status.heartbeat_title')") {{ $t('transmitters.status.heartbeat') }}
        el-radio-button(label="full_tx" :title="$t('transmitters.status.full_tx_title')") {{ $t('transmitters.status.full_tx') }}
        el-radio-button(label="combined" :title="$t('transmitters.status.combined_title')") {{ $t('transmitters.status.combined') }}
      .filters
        .status-header
          .status-card
            .status-card-column
              .status-card-row
                i.el-icon-time.status-icon
                span.status-label {{ $t('transmitters.status.last_transmission') }}
              span.status-value {{ transmitter.last_transmission ? buildDateTime(transmitter.last_transmission) : '-' }}
        FilterDatePicker(
          v-model="dateRange"
          :resetSignal="resetKey"
          @onChange="handleChange"
        )
        el-button.el-icon-refresh-left.mr-2.cursor-pointer(
          style="font-size: 35px; margin-right: 0px;"
          @click="reloadData()"
        )
      br
      PowerChart(
        :chartData="transmitterInfos"
        :pairs="activePairs"
        @onZoom="handleZoom"
      )
</template>

<script>
import { mapActions, mapMutations, mapState } from "vuex";
import FilterDatePicker from "@components/_shared/FilterDatePicker";
import PowerProfileStatus from "@components/_shared/PowerProfileStatus";
import { customValidations } from "@utils/customValidators";
import { transmitterConfigsViewValidations } from "@utils/formValidations/transmitterConfigsViewValidations";
import PowerChart from "@components/charts/PowerChart.vue";

const GENERAL_INFO_PAIRS = [
  { key: "ext_voltage", label: "Ext Voltage" },
  { key: "bat_voltage", label: "Bat Voltage" },
];

const HEARTBEAT_PAIRS = [
  { key: "ext_pre_sleep", label: "Ext Pre-Sleep" },
  { key: "bat_pre_sleep", label: "Bat Pre-Sleep" },
  { key: "ext_pre_lora", label: "Ext Pre-Lora" },
  { key: "bat_pre_lora", label: "Bat Pre-Lora" },
];

const FULL_TX_PAIRS = [
  ...HEARTBEAT_PAIRS,
  { key: "ext_sensor_start", label: "Ext Sensor Start" },
  { key: "bat_sensor_start", label: "Bat Sensor Start" },
  { key: "ext_sensor_end", label: "Ext Sensor End" },
  { key: "bat_sensor_end", label: "Bat Sensor End" },
];

export default {
  name: "EditTransmitterConfig",
  mixins: [transmitterConfigsViewValidations, customValidations],

  components: { FilterDatePicker, PowerProfileStatus, PowerChart },

  data() {
    return {
      activeTab: "status",
      form_config: {
        power_profile_id: null,
        execution_count: 1,
      },
      loadingForm: false,
      loadingSubmitBtn: false,
      powerProfiles: [],
      refreshing: false,
            transmitterInfos: [],
      dateRange: [],
      category: "general_info",
      loadingStatistics: false,
      resetKey: null,
    };
  },

  computed: {
    ...mapState("auth", ["user"]),
    ...mapState("transmitter", ["transmitter"]),

    activePairs() {
      if (this.category === "full_tx" || this.category === "combined") return FULL_TX_PAIRS;
      if (this.category === "heartbeat") return HEARTBEAT_PAIRS;
      return GENERAL_INFO_PAIRS;
    },

    firmwareOutdated() {
      return (
        this.transmitter &&
        this.transmitter.firmware_version &&
        this.transmitter.site_latest_firmware_build_time &&
        this.transmitter.firmware_version !== this.transmitter.site_latest_firmware_build_time
      );
    },
  },

  watch: {
    transmitter() {
      this.handleGetFormData();
    },
  },

  async created() {
    this.activeTab = "status";
    this.loadingForm = true;
    await this.handleGetPowerProfiles();
    await this.handleChange(this.dateRange);
    this.handleGetFormData();
    this.loadingForm = false;
  },

  methods: {
    ...mapActions("transmitterInfo", ["getSensorTransmitterInfo"]),
    ...mapMutations("transmitterInfo", ["clearTransmitterInfoCache"]),
    ...mapMutations("transmitter", ["setShouldFetchTransmitters", "setTransmitter"]),
    ...mapActions("transmitter", ["updateTransmitterConfig", "getPowerProfiles", "getTransmitter"]),

    ...mapActions("transmitterInfo", ["getSensorTransmitterInfo"]),

    ...mapMutations("transmitterInfo", ["clearTransmitterInfoCache"]),


    handleCategoryChange() {
      this.handleChange(this.dateRange);
    },

    reloadData() {
      this.clearTransmitterInfoCache();
      this.resetKey = Date.now();
      this.handleChange(this.dateRange);
    },

    async handleZoom([start, end]) {
      this.dateRange = [start, end];
      await this.handleChange(this.dateRange);
    },

    async handleChange(values) {
      this.transmitterInfos = [];

      if (!values || values.length === 0) {
        return;
      }

      this.loadingStatistics = true;

      const dateParams = `&by_date_range[start_date]=${values[0]}&by_date_range[end_date]=${values[1]}`;
      const base = `?by_sensor_transmitter_id=${this.transmitter.id}`;

      this.dateRange = values;

      if (this.category === "combined") {
        const [heartbeat, fullTx] = await Promise.all([
          this.getSensorTransmitterInfo(`${base}&by_category=heartbeat${dateParams}`),
          this.getSensorTransmitterInfo(`${base}&by_category=full_tx${dateParams}`),
        ]);

        this.transmitterInfos = [...heartbeat, ...fullTx].sort(
          (a, b) => new Date(a.created_at) - new Date(b.created_at)
        );
      } else {
        this.transmitterInfos = await this.getSensorTransmitterInfo(
          `${base}&by_category=${this.category}${dateParams}`
        );
      }

      this.loadingStatistics = false;
    },




    buildDateTime(dateValue) {
      const localDate = new Date(dateValue);
      const months = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];
      const day = localDate.getDate().toString().padStart(2, "0");
      const monthName = months[localDate.getMonth()];
      const year = localDate.getFullYear();
      const timeStr = localDate.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit", second: "2-digit", hour12: true });
      return `${timeStr} on ${day}-${monthName}-${year}`;
    },

    async refreshTransmitter() {
      if (!this.transmitter) return;
      this.refreshing = true;
      const data = await this.getTransmitter(this.transmitter.id);
      this.setTransmitter(data.data);
      this.refreshing = false;
    },

    handleGetFormData() {
      if (!this.transmitter) return;
      this.form_config = {
        id: this.transmitter.id,
        power_profile_id: this.transmitter.power_profile_id != null ? String(this.transmitter.power_profile_id) : null,
        execution_count: this.transmitter.power_profile_execution_count ?? 1,
      };
    },

    async handleGetPowerProfiles() {
      const powerProfiles = await this.getPowerProfiles("?page_size=1000");
      this.powerProfiles = powerProfiles.data;
    },

    handleEditTransmitterConfig(formName) {
      this.loadingSubmitBtn = true;
      const form = this.$refs[formName];

      form.validate(async (valid) => {
        if (valid) {
          let params = { ...this.form_config };
          this.updateTransmitterConfig(params)
            .then(() => {
              this.setShouldFetchTransmitters(true);
            })
            .finally(() => {
              this.loadingSubmitBtn = false;
            });
        } else {
          this.loadingSubmitBtn = false;
        }
      });
    },
  },
};
</script>

<style lang="scss" scoped>

.tabs {
    border-bottom: 1px solid #e4e7ec;
    height: 39px;
    margin-bottom: 15px;
}
.status-tab {
  .status-card {
    background: #f7f9fa;
    border-radius: 8px;
    padding: 14px 18px;
    border: 1px solid #e4e7ec;
    margin-bottom: 16px;
    gap: 15px;

    .status-card-column {
      display: flex;
      flex-direction: column;

      .status-card-row {
        display: flex;
        align-items: center;
        padding: 4px 0;
      }

      .status-icon {
        color: #909399;
        margin-right: 8px;
        font-size: 16px;
      }

      .status-label {
        font-weight: 600;
        color: #606266;
        margin-right: 8px;
        min-width: 160px;
      }

      .status-value {
        color: #303133;
      }
    }
  }
}

.status-header {
  margin-right: auto !important;
  .status-card {
    background: #f7f9fa;
    border-radius: 8px;
    padding: 14px 18px;
    border: 1px solid #e4e7ec;
    margin-bottom: 16px;
    gap: 15px;

    .status-card-column {
      display: flex;
      flex-direction: column;

      .status-card-row {
        display: flex;
        align-items: center;
        padding: 4px 0;
      }

      .status-icon {
        color: #909399;
        margin-right: 8px;
        font-size: 16px;
      }

      .status-label {
        font-weight: 600;
        color: #606266;
        margin-right: 8px;
        min-width: 160px;
      }

      .status-value {
        color: #303133;
      }
    }
  }
}

.el-select {
  width: 100%;
}

.field-hint {
  display: flex;
  align-items: center;
  font-size: 12px;
  color: #909399;
  margin-top: 4px;

  i {
    color: #e6a23c;
  }
}

.pps-hint {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  color: #909399;

  i {
    color: #e6a23c;
  }
}
</style>
